import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useSearchParams, useParams, useNavigate } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { setNotificationMessage } from '../../redux/slices/NotificationManagementSlice';
import { fetchAllStations } from '../../redux/slices/RevenueManagementStationSlice';
import AutoCompleteSelectField from '../../shared-components/auto-complete-select-field/AutoCompleteSelectField';
// import PageTitle from '../../shared-components/page-title/PageTitle';
import MyButton from '../../shared-components/button/Button';
import CustomInput from '../../shared-components/custom-input/CustomInput';
import ConfirmBox from '../../shared-components/modal-box/ConfirmBox';
import ModalBox from '../../shared-components/modal-box/ModalBox';
import SuccessNotification from '../../shared-components/success-notification/SuccessNotification';
import SwitchButton from '../../shared-components/switch-button-field/SwitchButton';
import { TooltipIcon } from '../../shared-components/tooltip/TooltipIcon';
import { permissionCheck } from '../../shared-functions/JWTDocodeFunction';
import revenueApi from '../../utils/api/revenue';

import AccountRevenueSharing from './components/AccountRevenueSharing';
import ManagementAccountRevenue from './components/ManageAccountRevenue';
import ManagementStationRevenue from './components/ManageStationRevenue';

interface UtilityCostFormState {
  energyCostEnabled: boolean;
  partnerRevenueSharePercentage: number | null;
  cpoRevenueSharePercentage: number | null;
}

const defaultUtilityCost: UtilityCostFormState = {
  energyCostEnabled: false,
  partnerRevenueSharePercentage: null,
  cpoRevenueSharePercentage: null,
};

/**
 * User Management page function component
 * @returns {any} The rendered component
 */
function RevenueManagement() {
  const { t } = useTranslation('revenue');
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { currentPage: routePage } = useParams<{ currentPage?: string }>();
  const [searchParameters, setSearchParameters] = useSearchParams();
  const pageParameter = searchParameters.get('currentPage') || 'account';
  const [openTab, setOpenTab] = useState<number>();

  /**
   * Function to get initial page
   * @returns {string} The initial page
   */
  const getInitialPage = () => {
    if (routePage === 'partner') return 'account';
    if (routePage === 'station') return 'station';
    return pageParameter;
  };

  const [currentPage, setCurrentPage] = useState<string>(getInitialPage());

  /**
   * Function to set current page and update search parameters and route path
   * @param {any} value - The value
   */
  const handleSetCurrentPage = (value: React.SetStateAction<string>) => {
    const nextValue = typeof value === 'function' ? value(currentPage) : value;
    setCurrentPage(nextValue);

    // Sync search parameter for backward compatibility if needed
    setSearchParameters((previous) => {
      const updated = new URLSearchParams(previous.toString());
      updated.set('currentPage', nextValue);
      return updated;
    });

    // Also navigate to the matching route path
    const routeName = nextValue === 'account' ? 'partner' : 'station';
    const basePath = location.pathname.includes('revenue-sharing')
      ? '/ev-admin/revenue/revenue-sharing'
      : '/ev-admin/configuration/revenue-management';
    navigate(`${basePath}/${routeName}`, { replace: true });
  };

  // Sync route param with component state
  useEffect(() => {
    if (routePage === 'partner' && currentPage !== 'account') {
      setCurrentPage('account');
    } else if (routePage === 'station' && currentPage !== 'station') {
      setCurrentPage('station');
    } else if (!routePage) {
      // If we are at the base path, redirect to default '/partner' or current page
      const routeName = currentPage === 'account' ? 'partner' : 'station';
      const basePath = location.pathname.includes('revenue-sharing')
        ? '/ev-admin/revenue/revenue-sharing'
        : '/ev-admin/configuration/revenue-management';
      navigate(`${basePath}/${routeName}`, { replace: true });
    }
  }, [routePage, navigate, location.pathname, currentPage]);

  useEffect(() => {
    if (pageParameter && pageParameter !== currentPage && !routePage) {
      setCurrentPage(pageParameter);
    }
  }, [pageParameter, routePage]);

  const [selectedAccountId, setSelectedAccountId] = useState<string | number>('');

  const [utilityCost, setUtilityCost] = useState<UtilityCostFormState>(defaultUtilityCost);
  const [utilityCostLoading, setUtilityCostLoading] = useState(false);
  const [utilityCostSubmitting, setUtilityCostSubmitting] = useState(false);
  const [utilityCostConfirmBox, setUtilityCostConfirmBox] = useState(false);
  const [utilityCostSuccessModal, setUtilityCostSuccessModal] = useState(false);

  useEffect(() => {
    /**
     * Function to fetch utility cost defaults and seed the form state
     * @returns {Promise<void>} Resolves once the utility cost defaults are loaded
     */
    const fetchUtilityCost = async () => {
      setUtilityCostLoading(true);
      try {
        const response = await revenueApi.fetchUtilityCost();
        const fetchedUtilityCost: UtilityCostFormState = {
          energyCostEnabled: !!response?.data?.energyCostEnabled,
          partnerRevenueSharePercentage: response?.data?.partnerRevenuePercentage ?? null,
          cpoRevenueSharePercentage: response?.data?.cpoRevenuePercentage ?? null,
        };
        setUtilityCost(fetchedUtilityCost);
      } catch (error: any) {
        dispatch(
          setNotificationMessage({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 500,
              message: error?.response?.data?.message || 'Something went wrong!',
            },
          }),
        );
      } finally {
        setUtilityCostLoading(false);
      }
    };

    fetchUtilityCost();
  }, []);

  /**
   * Function to toggle the utility cost enabled state
   * @param {boolean} checked - Whether the utility cost switch is on
   */
  const handleToggleUtilityCost = (checked: boolean) => {
    setUtilityCost((previous) => ({ ...previous, energyCostEnabled: checked }));
  };

  /**
   * Function to update a utility cost default value. Partner and CPO
   * shares are kept balanced to always sum to 100 - editing one field
   * auto-fills the other with the remainder.
   * @param {string} name - The field name (partnerRevenueSharePercentage or cpoRevenueSharePercentage)
   * @param {string | number} value - The new field value
   */
  const handleChangeUtilityCostValue = (name: string, value: string | number) => {
    const numericValue = value === '' ? null : Number(value);
    const counterpartName =
      name === 'partnerRevenueSharePercentage'
        ? 'cpoRevenueSharePercentage'
        : 'partnerRevenueSharePercentage';
    const counterpartValue =
      numericValue === null ? null : Math.round((100 - numericValue) * 100) / 100;

    setUtilityCost((previous) => ({
      ...previous,
      [name]: numericValue,
      [counterpartName]: counterpartValue,
    }));
  };

  /**
   * Function to save the utility cost defaults. Called after the user
   * confirms the action in the confirmation prompt.
   * @returns {Promise<void>} Resolves once the save request completes
   */
  const confirmSubmitUtilityCost = async () => {
    setUtilityCostConfirmBox(false);
    setUtilityCostSubmitting(true);
    try {
      await revenueApi.updateUtilityCost(utilityCost);
      setUtilityCostSuccessModal(true);
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 500,
            message: error?.response?.data?.message || 'Something went wrong!',
          },
        }),
      );
    } finally {
      setUtilityCostSubmitting(false);
    }
  };

  /**
   * Function to open the confirmation prompt before saving the utility cost
   * defaults
   */
  const handleSubmitUtilityCost = () => {
    setUtilityCostConfirmBox(true);
  };

  const {
    accountList,
    page: revenueStationPage,
    limit,
  } = useAppSelector((state) => state.revenueStation);

  /**
   * Function to change account Id filter
   * @param {string} name - The name
   * @param {any} onChangeValue - The value
   */
  const handleChangeAccountId = (name: string, onChangeValue: string | number) => {
    setSelectedAccountId(onChangeValue);
    dispatch(
      fetchAllStations({
        page: revenueStationPage,
        limit,
        accountCustomId: onChangeValue.toString(),
      }),
    );
  };

  useEffect(() => {
    if (location.pathname.includes('revenue-sharing')) {
      setOpenTab(0);
    } else {
      setOpenTab(1);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (searchParameters.get('accountCustomId') || searchParameters.get('stationId')) {
      const parameters = new URLSearchParams(searchParameters);
      parameters.delete('accountCustomId');
      parameters.delete('stationId');
      setSearchParameters(parameters, { replace: true });
    }
  }, []);

  return (
    <div style={{ width: '100%', height: '100%' }}>
      <ConfirmBox
        open={utilityCostConfirmBox}
        title="Update Revenue Share"
        subTitle="Are you sure you want to save the utility cost defaults?"
        conformSubmit={confirmSubmitUtilityCost}
        closeModal={() => setUtilityCostConfirmBox(false)}
        submitLabel="Confirm"
      />
      <ModalBox
        open={utilityCostSuccessModal}
        title=""
        header={false}
        notification
        closeModal={() => setUtilityCostSuccessModal(false)}
        content={
          <SuccessNotification
            title="Utility cost updated successfully"
            description=""
            notificationCloseTimer={3000}
            onNotificationClose={() => setUtilityCostSuccessModal(false)}
          />
        }
      />
      <div className="container revenue-body">
        {false && (
          <div className="revenue-toggle-wrap">
            <AutoCompleteSelectField
              name=""
              inputLabel=""
              selectedValue={selectedAccountId}
              placeholder={t('accountID')}
              onChangeValue={handleChangeAccountId}
              options={accountList}
            />
          </div>
        )}
        <div className="table-content">
          {openTab === 0 && (
            <div className="table-content">
              {permissionCheck('cpoAccountRevenueSharingView') && <AccountRevenueSharing />}
            </div>
          )}
          {openTab === 1 && (
            <div className="table-content">
              <div className="table-content-top">
                <div className="utility-cost-wrapper">
                  <p className="utility-cost-title">
                    Add Utility Provider Cost directly to Partner Share
                    <span>
                      <TooltipIcon text="Toggle to enable utility cost" infoColor="info" />
                    </span>
                  </p>
                  <div className="utility-cost-switch">
                    <SwitchButton
                      checked={utilityCost.energyCostEnabled}
                      change={handleToggleUtilityCost}
                      disabled={utilityCostLoading}
                    />
                    <span>{utilityCost.energyCostEnabled ? 'Enabled' : 'Disabled'}</span>
                  </div>
                </div>
                <span className="utility-cost-divider" />
                <CustomInput
                  inputLabel="Partner Share"
                  name="partnerRevenueSharePercentage"
                  type="number"
                  value={utilityCost.partnerRevenueSharePercentage}
                  onChangeValue={handleChangeUtilityCostValue}
                  placeholder="Enter partner share"
                  maxValue={100}
                  disabled={utilityCostLoading}
                  requiredTooltip
                  tooltipText="Default utility cost applied for the partner"
                  decimalLimit={2}
                />
                <span className="utility-cost-divider" />
                <CustomInput
                  inputLabel="CPO Share"
                  name="cpoRevenueSharePercentage"
                  type="number"
                  value={utilityCost.cpoRevenueSharePercentage}
                  onChangeValue={handleChangeUtilityCostValue}
                  placeholder="Enter cpo share"
                  maxValue={100}
                  disabled={utilityCostLoading}
                  requiredTooltip
                  tooltipText="Default utility cost applied for the CPO"
                  decimalLimit={2}
                />
                <span className="utility-cost-divider" />
                <div className="utility-cost-actions">
                  <MyButton
                    label="Submit"
                    buttonType="primary"
                    onClickFunc={handleSubmitUtilityCost}
                    loading={utilityCostSubmitting}
                    disableButton={utilityCostSubmitting || utilityCostLoading}
                  />
                </div>
              </div>
              {currentPage === 'account' && permissionCheck('cpoAccountRevenueView') && (
                <ManagementAccountRevenue
                  currentPage={currentPage}
                  setCurrentPage={handleSetCurrentPage}
                />
              )}
              {currentPage === 'station' && permissionCheck('cpoStationRevenueView') && (
                <ManagementStationRevenue
                  currentPage={currentPage}
                  setCurrentPage={handleSetCurrentPage}
                  selectedAccountId={selectedAccountId}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default RevenueManagement;
